<template>
    <div class="dropdown-container" v-click-outside-nested="closeDropdown" :style="{ zIndex: isOpen ? 1000000 : 0 }">
        <slot name="trigger" :toggle="toggleDropdown" :is-open="isOpen">
            <button class="dropdown-trigger" @click="toggleDropdown">
                <div class="trigger-text">
                    {{ triggerText }}
                </div>
                <span class="chevron" :class="{ 'open': isOpen }">&#9662;</span>
            </button>
        </slot>

        <transition name="fade">
            <div v-if="isOpen" class="dropdown-menu" :class="`align-${align}`">
                <slot :toggle="toggleDropdown" :is-open="isOpen"></slot>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
    triggerText: {
        type: String,
        default: 'Actions'
    },
    align: {
        type: String,
        default: 'right',
        validator: (value) => ['left', 'center', 'right'].includes(value)
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true
    }

});

const isOpen = ref(false);

const toggleDropdown = (event) => {
    if (event) {
        // event.stopPropagation();
        isOpen.value = !isOpen.value;

    }

};

const closeDropdown = () => {
    if (isOpen.value && props.closeOnClickOutside) {
        isOpen.value = false;
    }
};

// Expose methods for parent components to use
defineExpose({
    toggleDropdown,
    closeDropdown,
    isOpen
});

// Custom directive for detecting clicks outside of an element
// const vClickOutside = {
//     mounted(el, binding) {
//         el._clickOutside = (event) => {
//             if (!(el === event.target || el.contains(event.target))) {
//                 binding.value(event);
//             }
//         };
//         document.addEventListener('click', el._clickOutside);
//     },
//     unmounted(el) {
//         document.removeEventListener('click', el._clickOutside);
//     }
// };
</script>

<style>
:root {
    /* Colors */
    --color-dropdown-menu-bg: white;
    --color-dropdown-menu-border: #e5e5e5;
    --color-dropdown-item-border-top: #e5e5e5;
    --color-dropdown-item-hover-bg: #f0f0f0;
    --color-dropdown-item-active-bg: #e8e8e8;

    /* Dropdown Container */
    --zs-dropdown-container-position: relative;
    --zs-dropdown-container-display: inline-block;
    --zs-dropdown-container-font-family: Arial, sans-serif;

    /* Dropdown Toggle */
    --zs-dropdown-toggle-padding: 0;
    --zs-dropdown-toggle-cursor: pointer;

    /* Dropdown Trigger */
    --zs-dropdown-trigger-bg-color: #F9F9F9;
    --zs-dropdown-trigger-border: 1px solid #F9F9F9;
    --zs-dropdown-trigger-border-radius: 5px;
    --zs-dropdown-trigger-font-size: 12px;
    --zs-dropdown-trigger-font-family: "Satoshi-Bold";
    --zs-dropdown-trigger-cursor: pointer;
    --zs-dropdown-trigger-max-height: 34px;

    /* Chevron */
    --zs-dropdown-chevron-transition: transform 0.3s ease;

    /* Dropdown Menu */
    --zs-dropdown-menu-position: absolute;
    --zs-dropdown-menu-top: 100%;
    --zs-dropdown-menu-left: 0;
    --zs-dropdown-menu-width: 100%;
    --zs-dropdown-menu-bg-color: var(--color-dropdown-menu-bg);
    --zs-dropdown-menu-border: 1px solid var(--color-dropdown-menu-border);
    --zs-dropdown-menu-overflow-y: auto;
    --zs-dropdown-menu-z-index: 1000000;
    --zs-dropdown-menu-border-radius-bottom: 0 0 6px 6px;

    /* Menu Top Positioning (when flipped) */
    --zs-dropdown-menu-top-bottom: calc(100% - 23px);
    --zs-dropdown-menu-top-border-radius: 6px 6px 0 0;
    --zs-dropdown-menu-top-no-label-bottom: 100%;

    /* Dropdown Items */
    --zs-dropdown-item-padding: 10px;
    --zs-dropdown-item-cursor: pointer;
    --zs-dropdown-item-border-top: 1px dashed var(--color-dropdown-item-border-top);

    /* Dropdown Item Sizes */
    --zs-dropdown-item-small-padding: 6px 10px;
    --zs-dropdown-item-small-font-size: 12px;
    --zs-dropdown-item-large-padding: 12px 10px;
    --zs-dropdown-item-large-font-size: 16px;

    /* Dropdown Item States */
    --zs-dropdown-item-hover-bg-color: var(--color-dropdown-item-hover-bg);
    --zs-dropdown-item-active-bg-color: var(--color-dropdown-item-active-bg);
}

.dropdown-container {
    position: var(--zs-dropdown-container-position, relative);
    display: var(--zs-dropdown-container-display, inline-block);
    font-family: var(--zs-dropdown-container-font-family, Arial, sans-serif);
}

.dropdown-trigger {
    background-color: var(--zs-dropdown-trigger-bg-color, #F9F9F9);
    border: var(--zs-dropdown-trigger-border, 1px solid #F9F9F9);
    border-radius: var(--zs-dropdown-trigger-border-radius, 5px);
    font-size: var(--zs-dropdown-trigger-font-size, 12px);
    font-family: var(--zs-dropdown-trigger-font-family, "Satoshi-Bold");
    cursor: var(--zs-dropdown-trigger-cursor, pointer);
    display: flex;
    align-items: center;
    max-height: var(--zs-dropdown-trigger-max-height, 34px);
}

/* Custom trigger wrapper styles */
.dropdown-container> :first-child {
    cursor: pointer;
}

.chevron {
    transition: var(--zs-dropdown-chevron-transition, transform 0.3s ease);
}

.chevron.open {
    transform: rotate(180deg);
}

.dropdown-menu {
    position: var(--zs-dropdown-menu-position, absolute);
    top: var(--zs-dropdown-menu-top, calc(100% + 5px));
    background: var(--zs-dropdown-menu-bg-color, white);
    border-radius: var(--zs-dropdown-menu-border-radius, 8px);
    box-shadow: var(--zs-dropdown-menu-box-shadow, 0 2px 10px rgba(0, 0, 0, 0.1));
    z-index: var(--zs-dropdown-menu-z-index, 1000);
    padding: var(--zs-dropdown-menu-padding, 8px 0);
}

.dropdown-menu.align-left {
    left: 0;
}

.dropdown-menu.align-center {
    left: 50%;
    transform: translateX(-50%);
}

.dropdown-menu.align-right {
    right: 0;
}

.dropdown-container .menu-item {
    display: block;
    width: var(--zs-dropdown-menu-item-width, 100%);
    text-align: var(--zs-dropdown-menu-item-text-align, left);
    padding: var(--zs-dropdown-menu-item-padding, 8px 16px);
    border: none;
    background: none;
    cursor: pointer;
    font-size: var(--zs-dropdown-menu-item-font-size, 14px);
    color: var(--zs-dropdown-menu-item-color, #333);
}

.dropdown-container .menu-item:hover {
    background-color: var(--zs-dropdown-menu-item-hover-bg-color, #f5f5f5);
}

.delete {
    color: var(--zs-dropdown-menu-item-delete-color, #ff6b6b);
}

/* Fade animations */
.fade-enter-active,
.fade-leave-active {
    transition: opacity var(--zs-dropdown-transition-duration, 0.2s) var(--zs-dropdown-transition-easing, ease),
        transform var(--zs-dropdown-transition-duration, 0.2s) var(--zs-dropdown-transition-easing, ease);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(var(--zs-dropdown-transform-offset, -5px));
}

/* Adjust transformed elements when using center alignment */
.align-center.fade-enter-from,
.align-center.fade-leave-to {
    transform: translateX(-50%) translateY(var(--zs-dropdown-transform-offset, -5px));
}

.align-center.fade-enter-active,
.align-center.fade-leave-active {
    transform: translateX(-50%);
}

.trigger-text {
    color: var(--zs-dropdown-trigger-text-color, #21425F);
}
</style>
